import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { CHAT_SOURCE_LABELS, type ChatSource, type ChatSourceKind } from "@gb/contracts";
import { cn } from "@/lib/utils";

/**
 * Citations, gathered by what kind of thing they are.
 *
 * A flat list is unreadable the moment an answer cites crew: "is everyone
 * cleared?" returns one row per person, and twenty-six near-identical rows bury
 * the manual and the contract that the answer actually turned on. Worse, there
 * is no visible seam — the site notes end and the crew begins with nothing
 * between them, so the list reads as one undifferentiated pile.
 *
 * Grouping does two things at once: it puts a heading where that seam is, and
 * it lets a group of twenty-six collapse to one line.
 */

/**
 * Most specific first, and **crew last**.
 *
 * A crew row is a person's certification record — real evidence, and almost
 * never the thing a reader wants to check first. Documents and manuals are what
 * an answer is usually argued from, so they open the list; crew is the long
 * tail that would otherwise push them off the screen.
 *
 * `manual` is a seeded vendor manual section, a `knowledge_base` row of its
 * own. An uploaded manual is a document, so it groups under documents here,
 * as it sits on its category's shelf in the Brain (`document-ai/0032`).
 */
const KIND_ORDER: readonly ChatSourceKind[] = [
  "document",
  "manual",
  "decision",
  "journal",
  "note",
  "crew",
];

/**
 * Above this many, a group starts closed.
 *
 * The threshold is about *noise*, not about kind: five journal entries are as
 * hard to read past as five crew records. Four was chosen to fit in the rail
 * beside an answer without scrolling. The sources sit under the answer now,
 * two or three cards across (2026-09-27, `product/0013`), and the number was
 * kept: the owner asked for them grouped as before.
 */
const COLLAPSE_ABOVE = 4;

export type SourceGroup = {
  kind: ChatSourceKind;
  label: string;
  items: ChatSource[];
};

/** The sources, bucketed and ordered. Empty kinds are absent, not empty. */
export function groupSources(sources: readonly ChatSource[]): SourceGroup[] {
  const byKind = new Map<ChatSourceKind, ChatSource[]>();
  for (const source of sources) {
    const existing = byKind.get(source.kind);
    if (existing) existing.push(source);
    else byKind.set(source.kind, [source]);
  }

  return KIND_ORDER.flatMap((kind) => {
    const items = byKind.get(kind);
    return items?.length
      ? [{ kind, label: CHAT_SOURCE_LABELS[kind], items }]
      : [];
  });
}

/**
 * One kind, with its count, foldable.
 *
 * `<details>` rather than a button and a state hook: it is disclosure, the
 * browser already knows how to do it, and it stays open across a re-render
 * without anything holding that.
 */
export function SourceGroupBlock({
  group,
  children,
  className,
  grid = false,
}: {
  group: SourceGroup;
  children: ReactNode;
  className?: string;
  /**
   * Cards side by side, as many across as fit at 300px or wider: one on a
   * narrow screen, two or three under an answer. The decision dialog keeps
   * the single column.
   */
  grid?: boolean;
}) {
  return (
    <details open={group.items.length <= COLLAPSE_ABOVE} className={cn("group", className)}>
      <summary
        className={cn(
          "flex cursor-pointer list-none items-center gap-2 py-1.5",
          "font-display text-[0.66rem] font-semibold tracking-[0.1em] text-muted uppercase",
          "hover:text-ink",
        )}
      >
        <ChevronDown
          size={13}
          aria-hidden
          className="shrink-0 transition-transform group-open:rotate-0 -rotate-90"
        />
        {group.label}
        <span className="rounded-full bg-subtle px-1.5 text-[0.7rem] font-semibold text-muted">
          {group.items.length}
        </span>
      </summary>
      <div
        className={cn(
          "pt-1 pb-2",
          grid ? "grid grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))] gap-3" : "flex flex-col gap-2",
        )}
      >
        {children}
      </div>
    </details>
  );
}
